import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { faqData, servicesData } from '../mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Mail, Phone, MapPin, Send, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useSEO } from '../hooks/useSEO';
import JsonLd from '../components/JsonLd';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  useSEO({
    title: 'Contact Us | Hei Web Creations Singapore',
    description:
      'Get in touch to start your web design project. Based in Singapore, we build premium websites with AI-powered workflows for SMEs and startups.',
  });

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
      { '@type': 'ListItem', position: 2, name: 'Contact', item: `${origin}/contact` },
    ],
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service_type: '',
    project_description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Inline banner state — { type: 'success' | 'error', message: string } | null
  const [feedback, setFeedback] = useState(null);

  // Math-based human check
  const [captcha, setCaptcha] = useState({ a: 0, b: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');

  const generateCaptcha = useCallback(() => {
    setCaptcha({
      a: Math.floor(Math.random() * 9) + 1,
      b: Math.floor(Math.random() * 9) + 1,
    });
    setCaptchaAnswer('');
  }, []);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);

    // Client-side human check
    if (parseInt(captchaAnswer, 10) !== captcha.a + captcha.b) {
      const msg = 'Please solve the math question correctly and try again.';
      setFeedback({ type: 'error', message: msg });
      toast({ title: 'Human check failed', description: msg, variant: 'destructive' });
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        service_type: formData.service_type,
        project_description: formData.project_description,
        captcha_answer: parseInt(captchaAnswer, 10),
        captcha_a: captcha.a,
        captcha_b: captcha.b,
      });

      const successMsg =
        "Thanks — we've received your enquiry and will get back to you within 24 hours.";
      setFeedback({ type: 'success', message: successMsg });
      toast({ title: 'Message Sent!', description: successMsg });
      setFormData({ name: '', email: '', service_type: '', project_description: '' });
      generateCaptcha();
    } catch (error) {
      const message =
        error.response?.data?.detail ||
        'Something went wrong. Please try again in a moment.';
      setFeedback({ type: 'error', message });
      toast({ title: 'Submission failed', description: message, variant: 'destructive' });
      generateCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="pt-32 bg-white" data-testid="contact-page">
      <JsonLd id="breadcrumb-contact" data={breadcrumbSchema} />
      <JsonLd id="faq-contact" data={faqSchema} />
      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              Let's Create Together
            </h1>
            <p className="text-xl md:text-2xl text-gray-500 leading-relaxed">
              Ready to start your project? Get in touch and let's discuss how we can bring your vision to life.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-8">
                Get in Touch
              </h2>
              <p className="text-lg text-gray-600 mb-12 leading-relaxed">
                Whether you have a project in mind or just want to explore possibilities, we'd love to hear from you. Reach out and let's start a conversation.
              </p>

              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C8102E] flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">Email</h3>
                    <a
                      href="mailto:hello@heiwebcreations.com"
                      className="text-gray-600 hover:text-[#C8102E] transition-colors"
                    >
                      hello@heiwebcreations.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C8102E] flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">Phone</h3>
                    <a
                      href="tel:+6598883331"
                      className="text-gray-600 hover:text-[#C8102E] transition-colors"
                    >
                      +65 9888 3331
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C8102E] flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">Location</h3>
                    <p className="text-gray-600">100 Peck Seah St, #07-07 Singapore 079333</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-8 bg-gradient-to-br from-[#C8102E] to-[#A00E26] rounded-2xl text-white">
                <h3 className="text-2xl font-bold mb-3">Response Time</h3>
                <p className="text-white/90 leading-relaxed">
                  We typically respond to all inquiries within 24 hours. For urgent matters, please mention it in your message.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100" data-testid="contact-form">
                {feedback && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    role="status"
                    aria-live="polite"
                    data-testid={`contact-feedback-${feedback.type}`}
                    className={`mb-6 flex items-start gap-3 rounded-xl px-4 py-3 border ${
                      feedback.type === 'success'
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                  >
                    {feedback.type === 'success' ? (
                      <CheckCircle2 size={20} className="flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                    )}
                    <p className="text-sm leading-relaxed">{feedback.message}</p>
                  </motion.div>
                )}
                <h3 className="text-3xl font-bold text-[#1A1A1A] mb-8">Send us a message</h3>

                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name" className="text-[#1A1A1A] font-semibold mb-2 block">
                      Name <span className="text-[#C8102E]">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300 focus:border-[#C8102E] focus:ring-[#C8102E]"
                      data-testid="contact-name-input"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-[#1A1A1A] font-semibold mb-2 block">
                      Email <span className="text-[#C8102E]">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300 focus:border-[#C8102E] focus:ring-[#C8102E]"
                      data-testid="contact-email-input"
                    />
                  </div>

                  {/* Service Type */}
                  <div>
                    <Label htmlFor="service_type" className="text-[#1A1A1A] font-semibold mb-2 block">
                      Service Type <span className="text-[#C8102E]">*</span>
                    </Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(value) => handleSelectChange('service_type', value)}
                      required
                    >
                      <SelectTrigger
                        className="border-gray-300 focus:border-[#C8102E] focus:ring-[#C8102E]"
                        data-testid="contact-service-select"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {servicesData.map((service) => (
                          <SelectItem key={service.id} value={service.title}>
                            {service.title}
                          </SelectItem>
                        ))}
                        <SelectItem value="Other">Other / Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Project Description */}
                  <div>
                    <Label htmlFor="project_description" className="text-[#1A1A1A] font-semibold mb-2 block">
                      Project Description <span className="text-[#C8102E]">*</span>
                    </Label>
                    <Textarea
                      id="project_description"
                      name="project_description"
                      value={formData.project_description}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      placeholder="Tell us about your project, goals, and any specific requirements..."
                      className="border-gray-300 focus:border-[#C8102E] focus:ring-[#C8102E]"
                      data-testid="contact-description-input"
                    />
                  </div>

                  {/* Human Check (Math Captcha) */}
                  <div className="bg-[#FAFAFA] rounded-xl p-5 border border-gray-100">
                    <Label htmlFor="captcha" className="text-[#1A1A1A] font-semibold mb-3 block">
                      Human Check <span className="text-[#C8102E]">*</span>
                    </Label>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 text-lg font-semibold text-[#1A1A1A]">
                        <span data-testid="captcha-question">{captcha.a} + {captcha.b} =</span>
                      </div>
                      <Input
                        id="captcha"
                        name="captcha"
                        type="number"
                        value={captchaAnswer}
                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                        required
                        placeholder="?"
                        className="w-24 border-gray-300 focus:border-[#C8102E] focus:ring-[#C8102E]"
                        data-testid="captcha-input"
                      />
                      <button
                        type="button"
                        onClick={generateCaptcha}
                        className="text-gray-400 hover:text-[#C8102E] transition-colors p-2"
                        aria-label="New question"
                        data-testid="captcha-refresh"
                      >
                        <RefreshCw size={18} />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Solve the math question to verify you're human.</p>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-8 bg-[#C8102E] hover:bg-[#A00E26] text-white py-6 text-lg rounded-full transition-all duration-300 hover:shadow-lg disabled:opacity-50"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={20} className="ml-2" />
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-bold text-[#1A1A1A] mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find answers to common questions about our services and process
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-gray-50 rounded-xl px-6 border-0"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-[#1A1A1A] hover:text-[#C8102E] py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
